import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Card } from 'primeng/card';
import { InputText } from 'primeng/inputtext';
import { Message } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { WebAnalyticsService } from '../../services/web-analytics.service';
import { WebAnalyticsUiService } from '../../services/web-analytics-ui.service';
import { WebAnalyticsUserRow } from '../../models/web-analytics-dashboard';
import { dashboardFiltersFromQuery } from '../../utils/web-analytics-route-sync';
import { CountryLabelComponent } from '../../components/country-label/country-label.component';

@Component({
  selector: 'app-web-analytics-users',
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ButtonModule,
    Card,
    InputText,
    Message,
    ProgressSpinnerModule,
    SkeletonModule,
    TableModule,
    CountryLabelComponent,
  ],
  templateUrl: './web-analytics-users.component.html',
  styleUrl: './web-analytics-users.component.scss',
})
export class WebAnalyticsUsersComponent implements OnInit {
  private readonly webAnalytics = inject(WebAnalyticsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly waUi = inject(WebAnalyticsUiService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly rows = signal<WebAnalyticsUserRow[]>([]);
  readonly totalCount = signal(0);

  searchInput = '';
  sortField = 'totalEvents';
  sortOrder: 1 | -1 = -1;
  first = 0;
  readonly pageSize = 25;

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    if (event.sortField != null && event.sortField !== '') {
      const sf = event.sortField;
      this.sortField = Array.isArray(sf) ? (sf[0] ?? this.sortField) : sf;
    }
    if (event.sortOrder !== undefined) {
      this.sortOrder = event.sortOrder === 1 ? 1 : -1;
    }
    this.load();
  }

  applySearch(): void {
    this.first = 0;
    this.load();
  }

  goUser(row: WebAnalyticsUserRow): void {
    void this.router.navigate(this.userPath(row.username), {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  userPath(username: string): string[] {
    return ['/users', encodeURIComponent(username)];
  }

  private load(): void {
    const f = dashboardFiltersFromQuery(this.route.snapshot.queryParamMap);
    if (!f?.startDate || !f.endDate) {
      this.error.set('Set a date range in the sidebar, then Apply.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.webAnalytics
      .getUsers({
        filters: f,
        search: this.searchInput.trim(),
        sortBy: this.sortField,
        sortDescending: this.sortOrder < 0,
        limit: this.pageSize,
        offset: this.first,
      })
      .subscribe({
        next: (res) => {
          this.rows.set(res?.users ?? []);
          this.totalCount.set(res?.totalCount ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.error.set(
            err instanceof Error ? err.message : 'Could not load users.',
          );
          this.loading.set(false);
        },
      });
  }
}
